import { action, observable, runInAction } from 'mobx';
import { ErrorInterface } from 'modules/core/api/globalApi';
import isEqual from 'lodash/isEqual';
import { priceListController } from './priceListController';
import {
  GetPriceListRequest,
  CreatePriceListRequest,
  EditPriceListRequest,
  GetPriceListStationaryRequest,
  PriceList,
  PriceListHistoryItem,
  OfficesPricebookSnapshot,
  OfficePriceBook,
  CompositionComplex,
} from '../api/priceListApi';

// Полная позиция со всем строками

type cachePriceListsForInvoicesType = {
  request: GetPriceListRequest,
  item: PriceList[],
}

type cachePriceListsStationaryForInvoicesType = {
  request: GetPriceListStationaryRequest,
  item: PriceList,
}

export interface PriceListStore {
  priceLists: PriceList[],
  partnerId: string, // partnerId - по какому партнёру в priceLists находятся данные (если вообще задано)
  serviceId: number, // serviceId - по какому сервису в priceLists находятся данные (если вообще задано)
  specificPriceList: PriceList, // конкретный прайс-лист, извлечённый из базы по id
  history: PriceListHistoryItem[], // история изменений прайс-листа
  error: any,
  currentPriceList: PriceList,
  showDeleted: boolean, // показывать или не показывать удалённые прайсы в списке

  cachePriceListsForInvoices: cachePriceListsForInvoicesType[],
  cachePriceListsStationaryForInvoices: cachePriceListsStationaryForInvoicesType[];

  sovcombankSnapshot: OfficesPricebookSnapshot | null;

  // буфер комплексов состава для переноса между прайс-листами (кнопки «Скопировать в
  // буфер» / «Вставить из буфера» в ComplexBundlerPanelV2). Живёт в сторе, а не в локальном
  // state панели, чтобы переживать переход пользователя с одного прайс-листа на другой
  compositionBuffer: CompositionComplex[] | null;
}

export const priceListStore: PriceListStore = observable({
  priceLists: [],
  partnerId: undefined,
  serviceId: undefined,
  specificPriceList: null,
  history: [],
  error: null,
  currentPriceList: null,
  showDeleted: false,
  cachePriceListsForInvoices: [],
  cachePriceListsStationaryForInvoices: [],
  sovcombankSnapshot: null,
  compositionBuffer: null,
} as PriceListStore);

export const priceListActions = {
  getPriceLists: async (request: GetPriceListRequest): Promise<void> => {
    priceListStore.partnerId = request?.partnerId;
    const response = await priceListController.getPriceLists(request);
    priceListActions.handleError(response);
    runInAction(() => {
      priceListStore.priceLists = response.data;
    });
  },

  getPriceListsForInvoices: async (request: GetPriceListRequest): Promise<void> => {
    priceListStore.partnerId = request?.partnerId;
    priceListStore.serviceId = request?.serviceId;
    const x:PriceList[] | undefined = priceListStore.cachePriceListsForInvoices
      .find((v) => isEqual(v.request, request))?.item;
    if (x) {
      runInAction(() => {
        priceListStore.priceLists = x;
      });
      return;
    }
    const response = await priceListController.getPriceListsForInvoices(request);
    priceListActions.handleError(response);
    if (response.errorData) {
      priceListActions.clearPriceLists();
    }
    runInAction(() => {
      priceListStore.priceLists = response.data;
    });
    priceListStore.cachePriceListsForInvoices.push({
      item: response?.data,
      request,
    });
  },

  getPriceListsStationaryForInvoices: async (request: GetPriceListStationaryRequest):
    Promise<PriceList | ErrorInterface> => {
    const x:PriceList | undefined = priceListStore.cachePriceListsStationaryForInvoices
      .find((v) => isEqual(v.request, request))?.item;
    if (x) return x;
    const response = await priceListController.getPriceListsStationaryForInvoices(request);
    if (response?.errorData) { return response?.errorData; }
    priceListStore.cachePriceListsStationaryForInvoices.push({
      item: response?.data,
      request,
    });

    return response?.data;
  },

  getPriceListFromList: async (id: string): Promise<void> => {
    const response = priceListStore.priceLists.find((v) => v.id === id);
    priceListActions.setCurrentPriceList(response || null);
  },

  getSpecificPriceList: async (id: string): Promise<void> => {
    const response = await priceListController.getSpecificPriceList(id);
    priceListActions.handleError(response);
    runInAction(() => {
      priceListStore.specificPriceList = response.data;
    });
  },

  getPriceListHistory: async (id: string): Promise<void> => {
    const response = await priceListController.getPriceListHistory(id);
    priceListActions.handleError(response);
    runInAction(() => {
      priceListStore.history = response.data;
    });
  },

  clearHistory: action(() => {
    priceListStore.history = [];
  }),

  createPriceList: async (request: CreatePriceListRequest): Promise<void> => {
    const response = await priceListController.createPriceList(request);
    priceListActions.handleError(response);
  },

  editPriceList: async (id: string, request: EditPriceListRequest): Promise<void> => {
    const response = await priceListController.editPriceList(id, request);
    priceListActions.handleError(response);
    priceListActions.setCurrentPriceList(response.data);
  },

  deletePriceList: async (id: string): Promise<void> => {
    const response = await priceListController.deletePriceList(id);
    priceListActions.handleError(response);
  },

  restorePriceList: async (id: string): Promise<void> => {
    const response = await priceListController.restorePriceList(id);
    priceListActions.handleError(response);
  },

  clearPriceLists: action(() => {
    priceListStore.priceLists = [];
    priceListStore.partnerId = null;
  }),

  clearCurrentPriceList: action(() => {
    priceListStore.currentPriceList = null;
  }),

  clearError: action(() => {
    priceListStore.error = null;
  }),

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      priceListStore.error = errorData;
      throw errorData;
    }
  }),

  setCurrentPriceList: action((priceList: PriceList) => {     
    priceListStore.currentPriceList = priceList;
  }),

  setShowDeleted: action(() => {     
    priceListStore.showDeleted = !priceListStore.showDeleted;
  }),

  getSovcombankSnapshot: async (): Promise<void> => {
    const response = await priceListController.getSovcombankSnapshot();
    priceListActions.handleError(response);
    runInAction(() => {
      priceListStore.sovcombankSnapshot = response.data;
    });
  },

  clearSovcombankSnapshot: action(() => {
    priceListStore.sovcombankSnapshot = null;
  }),

  copyCompositionToBuffer: action((complexes: CompositionComplex[]) => {
    priceListStore.compositionBuffer = complexes;
  }),

  clearCompositionBuffer: action(() => {
    priceListStore.compositionBuffer = null;
  }),
};
